(() => {
  if (window.FoldingLinkDemoVideos) return;

  const initDemoSwitcher = (root) => {
    if (root.dataset.foldinglinkAppDemosInit === 'true') return;

    const tabs = Array.from(root.querySelectorAll('[data-foldinglink-app-demo-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-foldinglink-app-demo-panel]'));
    const selectorRail = root.querySelector('.foldinglink-app-demo-videos__selectors');
    const featured = root.querySelector('.foldinglink-app-demo-videos__featured');
    const indicator = root.querySelector('[data-foldinglink-app-demo-indicator]');
    const previous = root.querySelector('[data-foldinglink-app-demo-previous]');
    const next = root.querySelector('[data-foldinglink-app-demo-next]');
    const counter = root.querySelector('[data-foldinglink-app-demo-counter]');
    if (!tabs.length || !panels.length) return;

    root.dataset.foldinglinkAppDemosInit = 'true';
    root.classList.add('is-enhanced');

    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const passiveOptions = { passive: true, signal: abortController.signal };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const initialIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
    let activeIndex = initialIndex;
    let touchStartX = 0;
    let touchStartY = 0;

    const positionIndicator = (tab) => {
      if (!tab || !indicator || indicator.offsetParent === null) return;
      indicator.style.width = `${tab.offsetWidth}px`;
      indicator.style.transform = `translate3d(${tab.offsetLeft}px, 0, 0)`;
    };

    const revealSelectedTab = (tab) => {
      if (!selectorRail || !tab) return;
      const maxScroll = Math.max(0, selectorRail.scrollWidth - selectorRail.clientWidth);
      if (maxScroll < 2) return;

      const visibleLeft = selectorRail.scrollLeft + 8;
      const visibleRight = selectorRail.scrollLeft + selectorRail.clientWidth - 8;
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      if (tabLeft >= visibleLeft && tabRight <= visibleRight) return;

      const target = Math.min(maxScroll, Math.max(0, tabLeft - ((selectorRail.clientWidth - tab.offsetWidth) / 2)));
      selectorRail.scrollTo({ left: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    const activate = (value, moveFocus = false) => {
      const requestedIndex = typeof value === 'number'
        ? value
        : tabs.findIndex((tab) => tab.dataset.foldinglinkAppDemoTab === value);
      activeIndex = Math.max(0, Math.min(tabs.length - 1, requestedIndex));
      const selectedTab = tabs[activeIndex];
      const selectedId = selectedTab.dataset.foldinglinkAppDemoTab;

      tabs.forEach((tab, index) => {
        const selected = index === activeIndex;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      panels.forEach((panel) => {
        const selected = panel.dataset.foldinglinkAppDemoPanel === selectedId;
        const owningTab = tabs.find((tab) => tab.dataset.foldinglinkAppDemoTab === panel.dataset.foldinglinkAppDemoPanel);
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        panel.setAttribute('role', 'tabpanel');
        if (owningTab?.id) panel.setAttribute('aria-labelledby', owningTab.id);
        if (!selected) panel.querySelectorAll('video').forEach((video) => video.pause());
      });

      if (counter) counter.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(tabs.length).padStart(2, '0')}`;
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === tabs.length - 1;

      requestAnimationFrame(() => {
        positionIndicator(selectedTab);
        revealSelectedTab(selectedTab);
      });

      if (moveFocus) selectedTab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index), options);
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
        if (event.key === 'ArrowRight') nextIndex = Math.min(tabs.length - 1, index + 1);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(nextIndex, true);
      }, options);
    });

    previous?.addEventListener('click', () => activate(activeIndex - 1), options);
    next?.addEventListener('click', () => activate(activeIndex + 1), options);

    featured?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, passiveOptions);
    featured?.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      activate(activeIndex + (deltaX < 0 ? 1 : -1));
    }, passiveOptions);

    const handleResize = () => {
      const selectedTab = tabs[activeIndex];
      requestAnimationFrame(() => positionIndicator(selectedTab));
    };
    window.addEventListener('resize', handleResize, passiveOptions);

    let resizeObserver;
    if (selectorRail && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(selectorRail);
    }

    root.foldinglinkSelectDemo = (id) => activate(id);
    root.foldinglinkDestroyDemo = () => {
      abortController.abort();
      resizeObserver?.disconnect();
      root.classList.remove('is-enhanced');
      delete root.dataset.foldinglinkAppDemosInit;
      delete root.foldinglinkSelectDemo;
      delete root.foldinglinkDestroyDemo;
    };

    activate(initialIndex);
  };

  const init = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-foldinglink-app-demos]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-foldinglink-app-demos]'));
    roots.forEach(initDemoSwitcher);
  };

  window.FoldingLinkDemoVideos = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-foldinglink-app-demos]')
      ? event.target
      : event.target.querySelector?.('[data-foldinglink-app-demos]');
    root?.foldinglinkDestroyDemo?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const root = event.target.closest?.('[data-foldinglink-app-demos]')
      || event.target.querySelector?.('[data-foldinglink-app-demos]');
    root?.foldinglinkSelectDemo?.(blockId);
  });
})();
